import { getApiUrl } from "../utils/general.js";
import axios from "axios";

const OpexService = {
  getOpexes: async (onlyAllocation) => {
    try {
      let url = `${getApiUrl()}/opex/getopexes`;
      if(onlyAllocation !== undefined){
        url = url + `?onlyAllocation=${onlyAllocation}`;
      }
      const { data } = await axios.get(
        url
      );
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  getOpex: async (opexId) => {
    try {
      const { data } = await axios.get(
        `${getApiUrl()}/opex/getopex?opexId=${opexId}`
      );
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  updateOpex: async (opexData) => {
    try {
      const { data } = await axios.put(
        `${getApiUrl()}/opex/updateopex`,
        opexData
      );
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  insertOpex: async (opexData) => {
    try {
      const { data } = await axios.post(
        `${getApiUrl()}/opex/insertopex`,
        opexData
      );
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  deleteOpex: async (opexId) => {
    try {
      const { data } = await axios.delete(
        `${getApiUrl()}/opex/deleteopex?opexId=${opexId}`
      );
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  // Field-level change methods
  getPendingChanges: async () => {
    try {
      const { data } = await axios.get(`${getApiUrl()}/opex/pending-changes`);
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  updatePendingChange: async (changeData) => {
    try {
      const payload = {
        ...changeData,
        oldValue: String(changeData.oldValue),
        newValue: String(changeData.newValue),
      };

      const { data } = await axios.post(
        `${getApiUrl()}/opex/pending-changes`,
        payload
      );
      return data;
    } catch (error) {
      console.error("Error sending pending change:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
      }
      throw error;
    }
  },

  getChangesHistory: async (filters = {}) => {
    try {
      let url = `${getApiUrl()}/opex/changes-history`;

      const params = new URLSearchParams();
      if (filters.startDate) params.append("StartDate", filters.startDate);
      if (filters.endDate) params.append("EndDate", filters.endDate);
      if (filters.status) params.append("Status", filters.status);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      const { data } = await axios.get(url);

      const formattedData = data.map((item) => ({
        ...item,
        modifiedAt: item.modifiedAt
          ? new Date(item.modifiedAt).toISOString()
          : null,
        handledAt: item.handledAt
          ? new Date(item.handledAt).toISOString()
          : null,
      }));

      return formattedData;
    } catch (error) {
      console.error("Error fetching change history:", error);
      console.error("Error response:", error.response?.data);
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  approvePendingChange: async (changeId, payload) => {
    try {
      const { data } = await axios.put(
        `${getApiUrl()}/opex/pending-changes/${changeId}/approve`,
        payload
      );
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  rejectPendingChange: async (changeId, payload) => {
    try {
      const { data } = await axios.put(
        `${getApiUrl()}/opex/pending-changes/${changeId}/reject`,
        payload
      );
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  // Record-level change methods
  insertPendingRecordChange: async (payload) => {
    try {
      const { data } = await axios.post(
        `${getApiUrl()}/opex/pending-record-changes`,
        payload
      );
      return data;
    } catch (error) {
      console.error("Error sending record-level pending change:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
      }
      throw error;
    }
  },

  getPendingRecordChanges: async (onlyAllocation) => {
    try {
      let url = `${getApiUrl()}/opex/pending-record-changes`;
      if(onlyAllocation !== undefined){
        url = url + `?onlyAllocation=${onlyAllocation}`;
      }
      const { data } = await axios.get(
        url
      );
      return data;
    } catch (error) {
      console.error("Error fetching pending record changes:", error);
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  approveRecordPendingChange: async (changeId, payload) => {
    try {
      const { data } = await axios.put(
        `${getApiUrl()}/opex/pending-record-changes/${changeId}/approve`,
        payload
      );
      return data;
    } catch (error) {
      console.error("Error approving record change:", error);
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  rejectRecordPendingChange: async (changeId, payload) => {
    try {
      const { data } = await axios.put(
        `${getApiUrl()}/opex/pending-record-changes/${changeId}/reject`,
        payload
      );
      return data;
    } catch (error) {
      console.error("Error rejecting record change:", error);
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  getRecordChangeHistory: async (filters = {}, onlyAllocation) => {
    try {
      let url = `${getApiUrl()}/opex/record-changes-history`;

      const params = new URLSearchParams();
      if (filters.startDate) params.append("StartDate", filters.startDate);
      if (filters.endDate) params.append("EndDate", filters.endDate);
      if (filters.status) params.append("Status", filters.status);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const { data } = await axios.get(url + `&onlyAllocation=${onlyAllocation}`);

      const formattedData = data.map((item) => ({
        ...item,
        modifiedAt: item.modifiedAt
          ? new Date(item.modifiedAt).toISOString()
          : null,
        handledAt: item.handledAt
          ? new Date(item.handledAt).toISOString()
          : null,
      }));

      return formattedData;
    } catch (error) {
      console.error("Error fetching record change history:", error);
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  getRecordsWithPendingChanges: async () => {
    try {
      const { data } = await axios.get(
        `${getApiUrl()}/opex/records-with-pending-changes`
      );
      return data;
    } catch (error) {
      console.error("Error fetching records with pending changes:", error);
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },

  setOpexRecentlyApproved: async (recordIds) => {
    try {
      const { data } = await axios.post(
        `${getApiUrl()}/opex/set-opex-recently-approved`,
        JSON.stringify(recordIds),
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  },
};

export default OpexService;
